/**
 * Types and functions for polygon set operations (union, intersection, subtraction)
 * @module
 */
import * as polygonclip from "polygon-clipping";
import { polyLine2Reverse } from "./polyLine2";
import type { PolyLine2Polygon, PolyLine2, PolyLine2MultiPolygon } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";

const ROUNDING_COEFFICIENT = 1000;

function polyLine2ToArray2D(pl: PolyLine2, round: boolean): polygonclip.Pair[] {
  const a = new Array(pl.length / 2);
  for (let i = 0, l = pl.length; i < l; i += 2) {
    if (round) {
      a[i >> 1] = [Math.round(pl[i] * ROUNDING_COEFFICIENT), Math.round(pl[i + 1] * ROUNDING_COEFFICIENT)];
    } else {
      a[i >> 1] = [pl[i], pl[i + 1]];
    }
  }

  a.reverse();
  return a;
}

function polyLine2ArrayToArray3D(pl: PolyLine2[][], round: boolean) {
  return pl.map((x) => x.map((y) => polyLine2ToArray2D(y, round)));
}

function array2DToPolyLine2(a: polygonclip.Pair[], round: boolean): PolyLine2 {
  const pl = polyLine2New(a.length * 2);

  for (let i = 0, l = a.length; i < l; i++) {
    const pt = a[i];
    if (round) {
      pl[2 * i] = pt[0] / ROUNDING_COEFFICIENT;
      pl[2 * i + 1] = pt[1] / ROUNDING_COEFFICIENT;
    } else {
      pl[2 * i] = pt[0];
      pl[2 * i + 1] = pt[1];
    }
  }

  polyLine2Reverse(pl);

  return pl;
}

function array3DToPolyLine2Array(pl: polygonclip.Pair[][][], round: boolean): PolyLine2Polygon[] {
  return pl.map((x) => x.map((y) => array2DToPolyLine2(y, round)));
}

export function polygonUnion(pls: PolyLine2Polygon[], round: boolean = true): PolyLine2Polygon[] {
  if (pls.length === 0) {
    return [];
  }
  const converted = polyLine2ArrayToArray3D(pls, round);
  try {
    return array3DToPolyLine2Array(polygonclip.union(converted[0], ...converted.slice(1)), round);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export function polygonIntersection(pls: PolyLine2Polygon[], round: boolean = true): PolyLine2Polygon[] {
  if (pls.length === 0) {
    return [];
  }
  const converted = polyLine2ArrayToArray3D(pls, round);
  try {
    return array3DToPolyLine2Array(polygonclip.intersection(converted[0], ...converted.slice(1)), round);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export function polygonIntersectionMulti(multiPls: PolyLine2MultiPolygon[], round: boolean = true): PolyLine2Polygon[] {
  const converted = multiPls.map((multiPl: PolyLine2MultiPolygon) => polyLine2ArrayToArray3D(multiPl, round));
  if (converted.length === 0) {
    return [];
  }
  try {
    const multiIntersection = polygonclip.intersection(converted[0], ...converted.slice(1));
    return array3DToPolyLine2Array(multiIntersection, round);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export function polygonSubtract(pls: PolyLine2Polygon[], round: boolean = true): PolyLine2Polygon[] {
  const converted = polyLine2ArrayToArray3D(pls, round);
  if (converted.length === 0) {
    return [];
  }
  try {
    return array3DToPolyLine2Array(polygonclip.difference(converted[0], ...converted.slice(1)), round);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export function polygonSubtractSimple(a: PolyLine2, b: PolyLine2, round: boolean = true): PolyLine2Polygon[] {
  return polygonSubtract([[a], [b]], round);
}

export function polygonIntersectSimple(a: PolyLine2, b: PolyLine2, round: boolean = true): PolyLine2Polygon {
  return polygonIntersection([[a], [b]], round).map((x) => x[0]);
}
